import React, { useState, useEffect } from 'react'
import { fromJS } from 'immutable'
import { useLazyQuery, useMutation } from '@apollo/client'
import { Button as MuiButton } from '@mui/material'
import Link from 'gui/components/link'
import TitleAutosuggest from 'gui/components/common/TitleAutosuggest'
import { CONTENT_TYPE_SERIES, CONTENT_TYPE_VIDEO } from 'services/content/content-types'
import { LANGUAGE_EN } from 'services/languages/constants'
import { GET_PRODUCT_REFERENCES } from './queries'
import { CREATE_CONTENT_REFERENCE, DELETE_CONTENT_REFERENCE } from './content-references/queries'

function ProductReferencesList ({ content, isViewOnlyMode }) {
  const [productReferences, setProductReferences] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedContent, setSelectedContent] = useState(null)

  const [getProductReferences] = useLazyQuery(GET_PRODUCT_REFERENCES)
  const [createContentReference] = useMutation(CREATE_CONTENT_REFERENCE)
  const [deleteContentReference] = useMutation(DELETE_CONTENT_REFERENCE)

  useEffect(() => {
    const fetchProductReferences = async () => {
      if (!content.get('contentId')) return

      setLoading(true)
      try {
        const { data } = await getProductReferences({
          variables: { productContentId: content.get('contentId') },
        })

        const referencesTo = data?.contentList?.content?.[0]?.contentReferencesTo || []

        // Use contentReferencesTo where contentFrom contains the content that references this product
        setProductReferences(referencesTo)
      } catch (error) {
        // Error fetching product references
      } finally {
        setLoading(false)
      }
    }

    fetchProductReferences()
  }, [content.get('contentId'), getProductReferences])

  const handleAddReference = async (newContent) => {
    try {
      // Create a reference where the selected content references this product
      const contentReference = {
        fromContentContentId: parseInt(newContent.contentId, 10),
        fromContentContentType: newContent.contentType,
        toContentContentId: content.get('contentId'),
        toContentContentType: content.get('contentType'),
        type: 'product',
      }

      await createContentReference({
        variables: { contentReference },
      })

      // Refresh the product references list
      const { data } = await getProductReferences({
        variables: { productContentId: content.get('contentId') },
        fetchPolicy: 'network-only', // Force fresh data from server
      })

      const referencesTo = data?.contentList?.content?.[0]?.contentReferencesTo || []
      setProductReferences(referencesTo)
      setSelectedContent(null)
    } catch (error) {
      // Error adding reference
    }
  }

  const handleRemoveReference = async (referenceId) => {
    try {
      await deleteContentReference({
        variables: { contentReference: { id: referenceId } },
      })

      // Refresh the product references list
      const { data } = await getProductReferences({
        variables: { productContentId: content.get('contentId') },
        fetchPolicy: 'network-only', // Force fresh data from server
      })

      const referencesTo = data?.contentList?.content?.[0]?.contentReferencesTo || []
      setProductReferences(referencesTo)
    } catch (error) {
      // Error removing reference
    }
  }

  if (loading) {
    return <div>Loading content that references this product...</div>
  }

  return (
    <div>
      {!isViewOnlyMode && (
        <div style={{ marginBottom: '20px' }}>
          <TitleAutosuggest
            label="Add Content Reference"
            title={selectedContent?.title || ''}
            selectedValue={selectedContent ? fromJS(selectedContent) : null}
            type={[CONTENT_TYPE_SERIES, CONTENT_TYPE_VIDEO]}
            onSelect={(newContent) => {
              const contentWithAdditionalFields = {
                ...newContent,
                language: newContent.language || newContent.lang || '',
                contentType: newContent.contentType || '',
                published: newContent.published || false,
              }
              setSelectedContent(contentWithAdditionalFields)
              handleAddReference(contentWithAdditionalFields)
            }}
            displaySelection={false}
            disabled={isViewOnlyMode}
          />
        </div>
      )}

      {productReferences.length > 0 ? (
        <div>
          <p>Content that references this product:</p>
          <div style={{ marginTop: '10px' }}>
            {productReferences.map((reference) => {
              // Select the best language variant for display
              const currentContentLanguage =
                content.get('language') || content.get('primaryLanguage') || LANGUAGE_EN
              let contentFrom = reference.contentFrom?.[0] // fallback to first
              if (reference.contentFrom && reference.contentFrom.length > 1) {
                contentFrom =
                  reference.contentFrom.find((item) => item.language === currentContentLanguage) ||
                  reference.contentFrom.find((item) => item.language === '_primary') ||
                  contentFrom
              }

              if (!contentFrom) return null

              return (
                <div
                  key={reference.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '8px',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#f9f9f9',
                  }}
                >
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <span style={{ fontWeight: 'bold', minWidth: '200px' }}>
                      {contentFrom.title}
                    </span>
                    <span style={{ minWidth: '80px' }}>
                      ID: <b>{contentFrom.contentId}</b>
                    </span>
                    <span style={{ minWidth: '100px' }}>
                      Type: <b>{contentFrom.contentType}</b>
                    </span>
                    <span style={{ minWidth: '80px' }}>
                      {contentFrom.published ? '✅ Published' : '❌ Unpublished'}
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Link href={`/content/${contentFrom.id}`}>
                      <MuiButton size="small">View</MuiButton>
                    </Link>
                    {!isViewOnlyMode && (
                      <MuiButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveReference(reference.id)}
                      >
                        Remove
                      </MuiButton>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        <div>No content currently references this product.</div>
      )}
    </div>
  )
}

export default ProductReferencesList
